import { ChangeDetectorRef, Component, EventEmitter, Output, inject, signal } from '@angular/core';
import { ChargingUser, UpdateChargingUserAddress, UpdateIdTagShippingAddress } from '../charging-user.model';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { IdTag, IdTagSearchEvent, LookupIdTag } from '../../id-tags/id-tag-model';
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  firstValueFrom,
  interval,
  map,
  Observable,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { NumberOnlyHelper } from 'src/app/shared/helpers/number-only';
import { Department, DepartmentSearchEvent } from '../../department/department.model';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { PageEvent } from '@angular/material/paginator';
import { ChargingUserService, CreateChargingUserIdTagRequest } from 'src/app/shared/services/charging-user.service';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { FeedbackFailComponent, SnackbarService } from 'ok-angular-shared/components';
import { RouterLink } from '@angular/router';
import { DepartmentChangeDialogComponent } from '../charging-user-department-change-dialog/charging-user-department-change-dialog.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ErrorMessage } from '../../../shared/models/errorMessage.model';
import OrganizationService from '../../../shared/services/organization.service';
import { MatDialogModule } from '@angular/material/dialog';
import { AdmG4AccountNumberComponent } from 'src/app/shared/components/adm-g4-account-number/adm-g4-account-number.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';
import {
  AddressChanged,
  DepAddressTypeaheadComponent,
  InitialAddress,
} from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { Guid } from 'guid-typescript';

interface OkAgreementNumberForm {
  agreementNumber: FormControl<number | null>;
}

interface OkAccountForm {
  invoicingAccountNumber: FormControl<number | null>;
}
interface AddressForm {
  chargingUserAddress: FormControl<string | null>;
  darId: FormControl<string | null>;
}

interface IdTagFormGroup {
  selectedPrintedId: FormControl<string | null>;
  printedId: FormControl<string | null>;
  idToken: FormControl<string | null>;
  idTagIdentifier: FormControl<string | null>;
}

@Component({
  selector: 'adm-charging-user-process-modal',
  templateUrl: './charging-user-process-modal.component.html',
  styleUrls: ['./charging-user-process-modal.component.scss'],
  imports: [
    RouterLink,
    CustomDropDownArrowDirective,
    FeedbackFailComponent,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    AsyncPipe,
    AdmG4AgreementNumberComponent,
    AdmG4AccountNumberComponent,
    DepAddressTypeaheadComponent,
  ],
})
export class ChargingUserProcessModalComponent {
  dialogRef = inject<MatDialogRef<ChargingUserProcessModalComponent>>(MatDialogRef);
  data = inject<{
    chargingUser: ChargingUser;
  }>(MAT_DIALOG_DATA);
  private idTagService = inject(IdTagService);
  private chargingUserService = inject(ChargingUserService);
  private departmentService = inject(DepartmentService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private organizationService = inject(OrganizationService);
  public chargingUser: ChargingUser = this.data.chargingUser;
  public chargingUserUpdated: ChargingUser | null = null;
  public idTagUserType: typeof IdTagUserType = IdTagUserType;
  public saving = signal(false);
  public savingAddress = false;
  public savingDepartment = false;
  public savingIdTag = false;
  public hasCompanyCar = this.chargingUser.idTagUserType.toString() === IdTagUserType[this.idTagUserType.CompanyCar].toString();
  public companyCarDetermined = this.chargingUser.idTagUserType.toString() !== IdTagUserType[this.idTagUserType.NotDetermined].toString();

  public chargingUserHasDepartment = this.data.chargingUser.departmentId > 0;
  public hasDeliveryAddress = signal(false);
  public selectedPrintedId: number | null = null;
  public editIdTagShippingAddress = false;
  public editDepartment = false;
  public addressFoundAndSelected = signal(false);
  public queriedPrintedId: number | null = null;
  public filteredLookupIdTags: LookupIdTag[];
  public departments: Department[] = [];
  public idTags$: Observable<IdTag[]>;
  public idTagSearchEvent: IdTagSearchEvent = { paging: new PageEvent(), chargingUserId: this.data.chargingUser.id } as IdTagSearchEvent;
  public idTagStatusEnum = IdTagStatusEnum;
  public initialAddress = signal<InitialAddress | null>(null);

  public agreementNumberFromDepartment = signal<number | null>(null);
  public missingIdTagGroup$: Observable<boolean>;
  private readonly idTagsRefresh$ = new Subject<void>();

  @Output() idTagAdded = new EventEmitter<ChargingUser>();

  get printedId() {
    return this.idTagFormGroup.controls.printedId;
  }
  get idToken() {
    return this.idTagFormGroup.controls.idToken;
  }
  get invoicingAccountNumber() {
    return this.okAccountFormGroup.controls['invoicingAccountNumber'];
  }
  get agreementNumber() {
    return this.okAgreementNumberFormGroup.controls['agreementNumber'];
  }
  get department() {
    return this.editDepartmentFormGroup.controls['department'];
  }

  public okAccountFormGroup: FormGroup<OkAccountForm> = new FormGroup<OkAccountForm>({
    invoicingAccountNumber: new FormControl<number | null>(
      this.data.chargingUser.invoicingAccountNumber === 0 ? null : this.data.chargingUser.invoicingAccountNumber
    ),
  });
  public okAgreementNumberFormGroup = new FormGroup<OkAgreementNumberForm>({
    agreementNumber: new FormControl<number | null>(null, [Validators.required]),
  });
  public editDepartmentFormGroup: FormGroup = new FormGroup({
    department: new FormControl<Department | null>(null),
  });
  public idTagFormGroup = new FormGroup<IdTagFormGroup>({
    selectedPrintedId: new FormControl<string | null>(null, [Validators.required]),
    printedId: new FormControl<string | null>(null, [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
      Validators.minLength(1),
      Validators.maxLength(6),
    ]),
    idToken: new FormControl<string | null>({ value: null, disabled: true }, [Validators.required]),
    idTagIdentifier: new FormControl<string | null>(null),
  });
  public addressForm: FormGroup<AddressForm> = new FormGroup<AddressForm>({
    chargingUserAddress: new FormControl(null, {
      validators: [Validators.required],
    }),
    darId: new FormControl<string | null>(null),
  });

  constructor() {
    if (!this.data?.chargingUser) {
      throw new Error('ChargingUser data is required');
    } else {
      this.initialAddress.set({
        fullAddress: this.chargingUser.idTagShippingAddress,
        darId: this.chargingUser.darId,
      });

      this.idTagFormGroup.valueChanges
        .pipe(
          startWith(this.idTagFormGroup.value),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(val => {
            if (!val) {
              return EMPTY;
            }

            if (val.printedId == null || !NumberOnlyHelper.isNumber(val.printedId)) {
              return EMPTY;
            }
            const printedId = Number(val.printedId);
            if (val.printedId?.toString() === this.queriedPrintedId?.toString()) {
              return EMPTY;
            }

            this.queriedPrintedId = printedId;

            return this.idTagService.lookupIdTags(printedId).pipe(
              tap(lookup => {
                this.filteredLookupIdTags = lookup;

                if (this.selectedPrintedId !== this.queriedPrintedId) {
                  this.idTagFormGroup.controls.selectedPrintedId.setValue('');
                  this.idTagFormGroup.controls.idToken.setValue('');
                  this.idTagFormGroup.controls.idTagIdentifier.setValue('');
                }

                if (this.filteredLookupIdTags.length === 0) {
                  this.printedId.setErrors({ foundNoPrintedIds: true });
                }
              })
            );
          })
        )
        .subscribe();

      const searchEvent = new DepartmentSearchEvent({
        organizationId: this.data.chargingUser.organizationId,
        paging: { pageSize: 1000 } as PageEvent,
      });

      this.departmentService
        .getDepartments(searchEvent)
        .pipe(
          tap(result => {
            this.departments = result.items;
            const chargingUsersDepartment = this.departments.find(d => d.id === this.chargingUser.departmentId);
            if (chargingUsersDepartment) {
              this.editDepartmentFormGroup.patchValue({ department: chargingUsersDepartment });
            }
            this.consolidateIdTagShippingAddress();
          })
        )
        .pipe(take(1))
        .subscribe();

      this.idTags$ = this.idTagsRefresh$.pipe(
        switchMap(() => this.idTagService.getIdTags(this.idTagSearchEvent)),
        map(result => result.items)
      );

      this.missingIdTagGroup$ = this.organizationService.getOrganization(this.chargingUser.organizationId).pipe(
        take(1),
        map(org => !org.idTagGroupName),
        tap(missing => {
          if (missing) {
            this.idTagFormGroup.disable();
          }
        })
      );
      this.initOkAgreementNumberFormGroup();
    }
  }

  public async initOkAgreementNumberFormGroup(agreementNumberFromDepartment: number | null = null) {
    let defaultAgreementNumber = this.chargingUser.agreementNumber;
    if (agreementNumberFromDepartment) {
      defaultAgreementNumber = agreementNumberFromDepartment;
    } else if (!defaultAgreementNumber && this.chargingUser.departmentId) {
      const department = await firstValueFrom(this.departmentService.getDepartment(this.chargingUser.departmentId));
      if (department?.agreementNumber) {
        this.agreementNumberFromDepartment.set(department.agreementNumber);
      }
    }
    this.okAgreementNumberFormGroup.controls.agreementNumber.setValue(defaultAgreementNumber);
    this.okAgreementNumberFormGroup.controls.agreementNumber.markAsUntouched();
  }

  public async addAgreementNumberFromDepartment() {
    if (this.agreementNumberFromDepartment) {
      await this.initOkAgreementNumberFormGroup(this.agreementNumberFromDepartment());
      await this.saveAgreementNumber();
    }
  }

  public selectAddress(address: AddressChanged | null) {
    if (!address || address.fullAddress === null) {
      return;
    }
    this.addressFoundAndSelected.set(true);
    this.addressForm.patchValue({
      chargingUserAddress: address.formatAddressToString,
      darId: address.darId?.toString() ?? null,
    });
  }

  public emptyAddress() {
    if (!this.addressFoundAndSelected()) {
      this.addressForm.controls.chargingUserAddress.setValue(null);
    }
  }

  public saveDepartment() {
    const previousDepartmentId = this.chargingUser.departmentId;
    this.chargingUserService
      .saveDepartment(this.department.value.id, this.chargingUser.id)
      .subscribe({
        next: async () => {
          this.editDepartment = false;
          this.chargingUser.departmentId = this.department.value.id;
          this.chargingUserHasDepartment = this.chargingUser.departmentId > 0;
          this.chargingUser.departmentName = this.department.value.name;
          this.snackbarService.success('Afdelingen blev succesfuldt gemt.');
          this.chargingUserUpdated = this.chargingUser;
          this.emptyAddress();
          if (this.chargingUser.departmentId !== previousDepartmentId && this.chargingUser.agreementNumber) {
            this.openDepartmentChangeModal();
          }
        },
      })
      .add(() => {
        interval(700).subscribe(() => {
          this.savingDepartment = false;
        });
      });
  }
  private openDepartmentChangeModal() {
    const dialogRef = this.dialog.open<DepartmentChangeDialogComponent, undefined, boolean>(DepartmentChangeDialogComponent);

    dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
      if (result) {
        this.agreementNumber.setValue(null);
        this.agreementNumber.markAsTouched();
      }
    });
  }

  public saveIdTagShippingAddress(deleteAddress = false) {
    this.savingAddress = true;
    let shippingAddress = this.addressForm.controls.chargingUserAddress.value;
    if (deleteAddress) {
      shippingAddress = null;
    }

    const shippingAddressToSave = shippingAddress ?? '';

    this.chargingUserService
      .saveIdTagShippingAddress(
        { shippingAddress: shippingAddressToSave, darId: this.addressForm.controls.darId.value ?? '' } as UpdateIdTagShippingAddress,
        this.chargingUser.id
      )
      .subscribe({
        next: async () => {
          this.initialAddress.set({
            fullAddress: shippingAddressToSave,
            darId: this.addressForm.controls.darId.value ? Guid.parse(this.addressForm.controls.darId.value) : undefined,
          });
          this.addressFoundAndSelected.set(false);
          this.editIdTagShippingAddress = false;
          this.chargingUser.idTagShippingAddress = shippingAddressToSave;
          this.snackbarService.success('Adressen blev succesfuldt gemt.');
          this.chargingUserUpdated = this.chargingUser;
          this.emptyAddress();
        },
      })
      .add(() => {
        interval(700).subscribe(() => {
          this.savingAddress = false;
          this.consolidateIdTagShippingAddress();
        });
      });
  }

  public close() {
    this.dialogRef.close({ chargingUserUpdated: this.chargingUserUpdated, openAddIdTagInfoModal: false });

    this.chargingUserUpdated = null;
  }

  public async deleteIdTag(idTag: IdTag) {
    idTag.status = IdTagStatusEnum.NoIdTagGroup;
    this.idTagService.setStatus(idTag).subscribe({
      next: () => {
        this.idTagsRefresh$.next();
        this.chargingUserUpdated = this.chargingUser;
      },
      error: err => {
        this.snackbarService.error(`Der skete fejl under sletning (${err.error.message})`);
      },
    });
  }

  public async saveInvoicingAccountNumber() {
    this.saving.set(true);
    try {
      await firstValueFrom(
        this.chargingUserService.saveInvoicingAccountNumber(this.invoicingAccountNumber.value?.toString() || '', this.chargingUser.id)
      );
      if (this.invoicingAccountNumber.value !== null) {
        this.chargingUser.invoicingAccountNumber = this.invoicingAccountNumber.value;
      }
      this.snackbarService.success('G4 Kontonr. blev succesfuldt gemt.');
      this.chargingUserUpdated = this.chargingUser;
    } finally {
      this.saving.set(false);
    }
  }

  public async saveAgreementNumber() {
    this.saving.set(true);
    const agreementNumber = this.agreementNumber.value?.toString() ?? '';
    try {
      await firstValueFrom(this.chargingUserService.saveAgreementNumber(agreementNumber, this.chargingUser.id));
      if (this.agreementNumber.value !== null) {
        this.chargingUser.agreementNumber = this.agreementNumber.value;
      }
      this.snackbarService.success('G4 Aftalenr. blev succesfuldt gemt.');
      this.chargingUserUpdated = this.chargingUser;
    } finally {
      this.saving.set(false);
    }
  }

  public async createIdTag() {
    this.savingIdTag = true;
    const printedIdControlValue = this.printedId.value;
    if (printedIdControlValue == null) {
      this.savingIdTag = false;
      return;
    }

    const printedId = Number(printedIdControlValue);
    const idTag: CreateChargingUserIdTagRequest = {
      departmentId: null,
      shippingAddress: this.chargingUser.idTagShippingAddress ?? '',
      description: this.chargingUser.name ?? '',
      attention: this.chargingUser.name ?? '',
      printedId: printedId,
      idToken: this.idToken.value ?? '',
      chargingUserId: this.chargingUser.id,
      idTagIdentifier: this.idTagFormGroup.controls.idTagIdentifier.value ?? null,
    };

    idTag.idToken = this.idTagFormGroup.controls.idToken.value ?? '';

    this.chargingUserService
      .createIdTagForChargingUser(idTag)
      .subscribe({
        next: async idTagId => {
          this.idTagsRefresh$.next();

          this.idTagFormGroup.reset();
          this.chargingUserUpdated = await firstValueFrom(this.chargingUserService.getChargingUser(this.chargingUser.id));

          this.chargingUser = this.chargingUserUpdated;
          this.idTagAdded.emit(this.chargingUser);
          this.consolidateIdTagShippingAddress();
          this.snackbarService.success('Brikken blev succesfuldt oprettet.');
        },
        error: error => {
          const errorMessage = error.error as ErrorMessage;
          if (errorMessage.code === 20009) {
            // This should not trigger, but is an error that the endpoint can return
            this.snackbarService.error(
              'Der er ikke tilknyttet en id tag gruppe til brugerens organisation. Derfor kan man ikke oprette brikker til denne bruger.'
            );
            return;
          }
          this.snackbarService.error('Der skete en fejl under oprettelse af brikken.');
        },
      })
      .add(() => {
        interval(700).subscribe(() => {
          this.savingIdTag = false;
        });
      });
  }

  public async selectIdTag(value) {
    this.selectedPrintedId = value;

    if (this.selectedPrintedId) {
      const idTagSearchEvent: IdTagSearchEvent = new IdTagSearchEvent(new PageEvent());
      idTagSearchEvent.printedId = this.selectedPrintedId;
      const existingIdTagsWithPrintedId = await firstValueFrom(this.idTagService.getIdTags(idTagSearchEvent));
      const printedIdAlreadyUsed = existingIdTagsWithPrintedId.items.length > 0;

      if (printedIdAlreadyUsed) {
        this.queriedPrintedId = this.selectedPrintedId;
        this.selectedPrintedId = null;
        this.idTagFormGroup.controls.idToken.setValue(null);
        this.printedId.setErrors({ printedIdAlreadyUsed: true });
      } else {
        const selectedIdTag = this.filteredLookupIdTags.find(i => i.printedId === this.selectedPrintedId);
        if (!selectedIdTag) {
          return;
        }

        this.idTagFormGroup.patchValue({
          printedId: selectedIdTag.printedId.toString(),
          selectedPrintedId: selectedIdTag.printedId.toString(),
          idToken: selectedIdTag.idToken,
          idTagIdentifier: selectedIdTag.idTagIdentifier,
        });
        this.printedId.setErrors(null);
      }
    }
  }

  getErrorMessage(controlName: string) {
    if (controlName === 'printedId' && this.idTagFormGroup.controls['printedId'].hasError('required')) {
      return `Indtast venligst et brik ID (helt tal)`;
    }
    if (controlName === 'idToken' && this.idTagFormGroup.controls['idToken'].hasError('required')) {
      return `Intet RFID - fremsøg og vælg en brik ID <br />for at få indsat et RFID`;
    }
    return '';
  }

  private consolidateIdTagShippingAddress() {
    if (!this.chargingUser.idTagShippingAddress && this.hasCompanyCar && this.chargingUser.departmentId) {
      this.chargingUser.idTagShippingAddress = this.departments.find(d => d.id === this.chargingUser.departmentId)?.address ?? '';
    }

    this.hasDeliveryAddress.set(this.chargingUser.idTagShippingAddress?.length > 0);
  }
  public openAddIdTagInfoModal() {
    this.dialogRef.close({ chargingUserUpdated: this.chargingUserUpdated, openAddIdTagInfoModal: true });
  }
}
